import classNames from 'classnames';
import { Button } from 'devextreme-react/button';
import { ScrollView } from 'devextreme-react/scroll-view';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { setPanelPined, setPanelVisible, setFilteredOrders } from '../../../contexts/slices/orders';
import { useCallback, useEffect, useRef, useState } from 'react';
import DataGrid, { Column, Selection, Scrolling, Paging, FilterRow, HeaderFilter } from 'devextreme-react/data-grid';
import { Tooltip } from 'devextreme-react/tooltip';
import { useApiOrders } from '../../../api/api.orders';
import moment from 'moment';
import { LoadPanel } from 'devextreme-react/load-panel';
import { FilterPopup, newToast } from '../..';
import { filter } from '../../../images/icons';

export const ContactPanelDetails = () => {
  const dispatch = useAppDispatch();
  const { PanelVisible, PanelPined } = useAppSelector((x) => x.globalOrders);
  const [Loading, setLoading] = useState(false);
  const { FilteredOrders, FiltersToOrders, FiltersToOrdersText } = useAppSelector((x) => x.globalOrders);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const [localPanelVisible, setLocalPanelVisible] = useState(PanelVisible);
  const { findOrder, GetOrdersByFilter } = useApiOrders();
  const gridRef = useRef<DataGrid>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handlePined = () => {
    dispatch(setPanelPined(!PanelPined));
  };

  const { isLarge, isMedium } = useScreenSize();

  const hideModalFilter = useCallback(() => {
    setShowFilterModal(false);
  }, [setShowFilterModal]);

  const handleOpenModalFilter = useCallback(() => {
    setShowFilterModal(true);
  }, [setShowFilterModal]);

  useEffect(() => {
    setLocalPanelVisible(PanelVisible);
  }, [PanelVisible]);

  useEffect(() => {
    if (isLarge || isMedium) {
      setLocalPanelVisible(PanelVisible);
      return;
    }
    setLocalPanelVisible(false);
    dispatch(setPanelVisible(false));
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (localPanelVisible && !PanelPined) {
      if (contenedorRef.current && !contenedorRef.current.contains(event.target as Node)) {
        handlePanelClose();
      }
    }
  };

  useEffect(() => {
    if (localPanelVisible && !PanelPined) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    // Limpia el evento cuando el componente se desmonta
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [localPanelVisible, PanelPined]);

  const handlePanelClose = () => {
    if (!PanelPined) {
      document.removeEventListener('click', handleClickOutside);
      dispatch(setPanelVisible(false));
    }
  };

  const handlePanelCloseInSide = () => {
    document.removeEventListener('click', handleClickOutside);
    dispatch(setPanelVisible(false));
  };

  // function handleRowClick(e: EventInfo<dxDataGrid<Order, any>>): void {
  //   const current = e.component.getSelectedRowsData();
  //   if (current.length === 0) return;
  //   findOrder(undefined, current[0].Ord_Id, setLoading);
  // }

  // Este es el evento que se ejecuta cuando cambia la fila seleccionada o el foco
  const handleFocusedRowChanged = (e) => {
    const selectedRow = e.row.data; // Aquí accedemos a la fila seleccionada

    if (selectedRow) {
      // Si la fila es válida, obtenemos su ID y ejecutamos `findOrder`
      const rowId = selectedRow.Ord_Id;
      findOrder(undefined, rowId, setLoading); // Ejecuta la función con el ID de la fila
    }
  };

  useEffect(() => {
    handleRefresh();
    // Asegurarnos de que el DataGrid reciba el foco cuando se monte el componente
    if (gridRef.current) {
      gridRef.current.instance.focus(); // Garantizamos que el DataGrid tenga el foco
    }
  }, []);

  const rowPrepared = (e) => {
    if (e.rowType === 'data') {
      const color = e.data.Se_Color;
      if (color && color.trim() !== '') {
        e.rowElement.style.color = color;
      }
      e.rowElement.setAttribute('id', `row-${e.data.Ord_Id}`);
    }
  };

  const handleRefresh = () => {
    if (FiltersToOrders) {
      setLoading(true);      
      GetOrdersByFilter(FiltersToOrders)
        .then((res) => {
          if (res.length === 0) {
            newToast('INFO', 'No se encontraron resultados para el filtro aplicado', 5);
          }
          dispatch(setFilteredOrders(res));
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }
    newToast('WARNING', 'No hay filtros aplicados actualmente', 5);
  };

  return (
    <div id='contact-panel' ref={contenedorRef} className={classNames({ panel: true, open: localPanelVisible, pin: PanelPined && (isLarge || isMedium), unpin: !PanelPined })}>
      <div className='data-wrapper'>
        <Toolbar className='panel-toolbar'>
          <ToolbarItem location='before'>
            <span className='contact-name value'>{FilteredOrders.length} Órdenes</span>
          </ToolbarItem>
          <ToolbarItem location='after' visible={isLarge || isMedium}>
            <Button icon={PanelPined ? 'pin' : 'unpin'} onClick={handlePined} />
          </ToolbarItem>
          <ToolbarItem location={'after'}>
            <div className='filter-button-container'>
              <Button id='filterButtonHeader' icon={filter} onClick={handleOpenModalFilter} hint={FiltersToOrdersText && FiltersToOrdersText.length > 0 ? '' : 'Filtrar Ordenes'} />
              {/* Badge de notificación para filtros aplicados - solo cuando hay filtros */}
              {(isLarge || isMedium) && FiltersToOrdersText && FiltersToOrdersText.length > 0 && <div className='filter-badge'>{FiltersToOrdersText.length}</div>}
              {/* Tooltip para mostrar filtros aplicados - solo cuando hay filtros */}
              {(isLarge || isMedium) && FiltersToOrdersText && FiltersToOrdersText.length > 0 && (
                <Tooltip
                  target='#filterButtonHeader'
                  showEvent='mouseenter'
                  hideEvent='mouseleave'
                  position='bottom'
                  contentComponent={() => (
                    <div className='filters-tooltip-content'>
                      <div className='filters-tooltip-title'>
                        <strong>Filtros aplicados:</strong>
                      </div>
                      {FiltersToOrdersText.map((filter, index) => (
                        <div key={index} className='filters-tooltip-item'>
                          {filter}
                        </div>
                      ))}
                    </div>
                  )}
                />
              )}
            </div>
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button icon='refresh' onClick={handleRefresh} />
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button icon='close' onClick={handlePanelCloseInSide} />
          </ToolbarItem>
        </Toolbar>
        <ScrollView className='panel-scroll'>
          <DataGrid dataSource={FilteredOrders} tabIndex={0} height='100%' onFocusedRowChanged={handleFocusedRowChanged} ref={gridRef} focusedRowEnabled={true} keyExpr='Ord_Id' id='currentOrders' onRowPrepared={rowPrepared} className='gridOrders compact-grid' noDataText='No hay órdenes para mostrar' hoverStateEnabled={true} repaintChangesOnly={true}>
            <Paging enabled={false} />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <Selection mode='single' />
            <Scrolling mode='virtual' rowRenderingMode='virtual' />
            <Column dataField='Ord_Numero' caption='ORDEN' width={100}>
              <HeaderFilter />
            </Column>
            <Column dataField='Pc_Nombre' caption='USUARIO' calculateCellValue={(rowData) => `${rowData.Pc_Nombre || ''} ${rowData.Pc_Apellido1 || ''} ${rowData.Pc_Apellido2 || ''}`} calculateDisplayValue={(rowData) => `${rowData.Pc_Nombre || ''} ${rowData.Pc_Apellido1 || ''} ${rowData.Pc_Apellido2 || ''}`}>
              <HeaderFilter />
            </Column>
            <Column dataField='Ord_FechaCreacion' caption='FECHA' width={100} dataType='date' format='dd/MM/yyyy' visible={false}>
              <HeaderFilter />
            </Column>
          </DataGrid>
        </ScrollView>
        <LoadPanel position={{ of: '#currentOrders' }} visible={Loading} showIndicator={true} />
      </div>
      {FilteredOrders.map((order) => (
        <Tooltip key={order.Ord_Id} target={`#row-${order.Ord_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='left'>
          <div className='toolTipFilter'>
            <div>
              <strong>Fecha de creación:</strong> {moment(order.Ord_FechaCreacion).format('DD/MM/YYYY')}
            </div>
            <div>
              <strong>Orden:</strong> {order.Ord_Numero}
            </div>
            <div>
              <strong>Usuario:</strong> {order.Pc_Nombre} {order.Pc_Apellido1 || ''} {order.Pc_Apellido2 || ''}
            </div>
          </div>
        </Tooltip>
      ))}
      <FilterPopup visible={showFilterModal} setVisible={hideModalFilter} />
    </div>
  );
};
